import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, AlertCircle, X, ClipboardCopy } from 'lucide-react';
import { ToastCtx, getUpgradeHandler } from './useToast';
import { copyDiagnostics } from './diagnostics';
import { translateRuntimeError } from './runtimeErrors';

// App-wide toast stack — replaces alert()/window.alert() call sites. Mount
// once near the root; call useToast() (in ./useToast.js) from anywhere to
// surface a message.
//
// Anchored bottom-center rather than bottom-right so it never collides with
// the floating AI chat launcher/panel that owns the bottom-right corner
// (ai/FloatingChatOverlay.jsx). Auto-dismiss timers pause while the pointer is
// over the stack or focus is inside it, so a long error — or an actionable
// toast's button (e.g. "Undo") — can be read and clicked before it clears.
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);
  const timers = useRef({}); // id -> setTimeout handle
  const toastsRef = useRef([]); // latest toasts, for resume without a stale closure

  useEffect(() => { toastsRef.current = toasts; }, [toasts]);
  useEffect(() => () => { Object.values(timers.current).forEach(clearTimeout); }, []);

  const dismiss = useCallback((id) => {
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    setToasts((ts) => ts.filter((t) => t.id !== id));
  }, []);

  const schedule = useCallback((id, ms) => {
    clearTimeout(timers.current[id]);
    timers.current[id] = setTimeout(() => dismiss(id), ms);
  }, [dismiss]);

  // action: optional { label, onClick } - rendered as a clickable affordance
  // inside the toast (e.g. "Built. Undo"). Given more time to read and act
  // on, an actionable toast stays up longer than a plain message.
  // correlationId: the backend's X-Correlation-Id for the request that
  // failed (see core/api.js's apiErrorFrom) - when present, the toast grows
  // its own "Copy diagnostics" affordance instead of the ordinary action slot,
  // since an error and an undo-style action never both apply to one toast.
  const push = useCallback((message, type, action, correlationId) => {
    const id = ++idRef.current;
    const duration = (action || correlationId) ? 8000 : 4500;
    setToasts((ts) => [...ts, { id, message, type, action, correlationId, duration }]);
    schedule(id, duration);
  }, [schedule]);

  const copyToastDiagnostics = useCallback((t) => {
    copyDiagnostics(t).then(
      () => push('Diagnostics copied to clipboard.', 'success'),
      () => push('Could not copy to clipboard.', 'error'),
    );
  }, [push]);

  // Pause every countdown while the user is interacting with the stack;
  // restart each from full duration on leave (a hover "resets" the timer).
  const pauseAll = useCallback(() => { Object.values(timers.current).forEach(clearTimeout); }, []);
  const resumeAll = useCallback(() => { toastsRef.current.forEach((t) => schedule(t.id, t.duration)); }, [schedule]);

  // error() accepts either a plain string or the Error thrown by core/api.js
  // (or anything error-shaped with a .message) - passing the Error straight
  // through, rather than requiring every call site to pluck out .message
  // first, is what lets correlationId ride along automatically.
  const api = useRef({
    success: (message, action) => push(message, 'success', action),
    error: (messageOrErr, action) => {
      const isErrObj = messageOrErr && typeof messageOrErr === 'object';
      const rawMessage = isErrObj ? (messageOrErr.message || 'Something went wrong') : messageOrErr;
      // Route every error toast through the runtime-error vocabulary layer
      // (§1.7) - a permission-denied or config-save-validator message speaks
      // in config keys/internal concepts otherwise (configLabels.js's own
      // module docstring is the fuller reasoning for why that's a real bug).
      const message = translateRuntimeError(rawMessage);
      // Auto-attach an "Upgrade" action when the caller didn't already pass
      // one and the backend flagged this as a plan-cap refusal (core/api.js's
      // apiErrorFrom carries the full response body as err.body) - see
      // useToast.js's getUpgradeHandler for why this call site needs no
      // per-caller change to get it.
      const resolvedAction = action || (isErrObj && messageOrErr.body?.cap_exceeded && getUpgradeHandler()
        ? { label: 'Upgrade', onClick: getUpgradeHandler() }
        : undefined);
      push(message, 'error', resolvedAction, isErrObj ? messageOrErr.correlationId : undefined);
    },
  }).current;

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div
        className="print:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-100 flex flex-col items-center gap-2"
        onMouseEnter={pauseAll}
        onMouseLeave={resumeAll}
        onFocus={pauseAll}
        onBlur={resumeAll}
      >
        {toasts.map((t) => (
          <div key={t.id}
            role={t.type === 'error' ? 'alert' : 'status'}
            aria-live={t.type === 'error' ? 'assertive' : 'polite'}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium transition-all duration-300 ${
            t.type === 'error'
              ? 'bg-red-50 dark:bg-red-950/80 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
              : 'bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200'
          }`}>
            {t.type === 'error' ? <AlertCircle size={15} className="text-red-500 shrink-0" /> : <CheckCircle2 size={15} className="text-emerald-500 shrink-0" />}
            <span>{t.message}</span>
            {t.action && (
              <button onClick={() => { t.action.onClick(); dismiss(t.id); }}
                className="font-semibold underline underline-offset-2 shrink-0">
                {t.action.label}
              </button>
            )}
            {t.correlationId && (
              <button onClick={() => copyToastDiagnostics(t)} title={`Correlation ID: ${t.correlationId}`}
                className="flex items-center gap-1 font-semibold underline underline-offset-2 shrink-0">
                <ClipboardCopy size={12} /> Copy diagnostics
              </button>
            )}
            <button onClick={() => dismiss(t.id)} aria-label="Dismiss" className="ml-1 text-subtle hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors"><X size={13} /></button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
